import React, { useState } from "react";
import { Link } from "react-router-dom";
import { DollarSign, Users, Megaphone, Briefcase, Check, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WAYS_TO_HELP = [
    { icon: <DollarSign size={28} />, title: "Donate", desc: "Every dollar directly funds construction of the park. Even small amounts add up fast in a community this size.", to: "/donate", testId: "help-donate-card" },
    { icon: <Users size={28} />, title: "Volunteer", desc: "Help with events, outreach, fundraising, planning, or hands-on construction when the time comes.", scrollTo: "volunteer-form", testId: "help-volunteer-card" },
    { icon: <Megaphone size={28} />, title: "Spread the Word", desc: "Follow us on social media and share our story. Your network could be our next big donor or sponsor.", to: "/contact", testId: "help-spread-card" },
    { icon: <Briefcase size={28} />, title: "Become a Sponsor", desc: "Businesses and organizations can partner with SLSA for powerful community recognition and impact.", to: "/sponsors", testId: "help-sponsor-card" },
];

const INTERESTS = [
    "Event planning & coordination",
    "Fundraising & grant writing",
    "Outreach & social media",
    "Park design input",
    "Construction & hands-on work",
    "Community organizing",
    "Photography / videography",
    "Other",
];

const GetInvolved = () => {
    const [volForm, setVolForm] = useState({ name: "", email: "", phone: "", interests: "", availability: "" });
    const [volLoading, setVolLoading] = useState(false);
    const [subEmail, setSubEmail] = useState("");
    const [subName, setSubName] = useState("");
    const [subLoading, setSubLoading] = useState(false);
    const [volSuccess, setVolSuccess] = useState(false);

    const handleVolSubmit = async (e) => {
        e.preventDefault();
        if (!volForm.name.trim() || !volForm.email.trim()) {
            toast.error("Name and email are required.");
            return;
        }
        setVolLoading(true);
        try {
            const res = await axios.post(`${API}/volunteer`, volForm);
            toast.success(res.data.message || "Signed up!");
            setVolSuccess(true);
        } catch (e) {
            toast.error(e.response?.status === 429 ? "Too many sign-ups from this connection — please try again in a few minutes." : "Something went wrong. Try again.");
        } finally {
            setVolLoading(false);
        }
    };

    const handleSubscribe = async (e) => {
        e.preventDefault();
        if (!subEmail.trim()) return;
        setSubLoading(true);
        try {
            const res = await axios.post(`${API}/subscribe`, { email: subEmail, name: subName });
            toast.success(res.data.message || "Subscribed!");
            setSubEmail("");
            setSubName("");
        } catch (e) {
            toast.error("Something went wrong. Try again.");
        } finally {
            setSubLoading(false);
        }
    };

    const updateVol = (f) => (e) => setVolForm(prev => ({ ...prev, [f]: e.target.value }));

    const inputCls = "w-full h-12 px-4 bg-white border-2 border-[#3D2008] font-manrope text-[#3D2008] placeholder-[#5C4A35]/50 focus:outline-none focus:border-[#C4522A] focus:shadow-[4px_4px_0px_0px_rgba(196,82,42,0.2)] transition-shadow";

    return (
        <div className="pt-16">
            {/* Hero */}
            <section
                className="relative py-24 px-4 bg-[#3D2008] overflow-hidden"
                style={{ backgroundImage: "url('https://images.pexels.com/photos/4734781/pexels-photo-4734781.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')", backgroundSize: "cover", backgroundPosition: "center" }}
                data-testid="getinvolved-hero"
            >
                <div className="absolute inset-0 bg-[#3D2008]/85" />
                <div className="relative z-10 max-w-7xl mx-auto">
                    <span className="font-marker text-[#C9A84C] text-2xl -rotate-1 inline-block mb-4">Be Part of the Build</span>
                    <h1 className="font-anton text-5xl sm:text-7xl lg:text-8xl text-[#F5EDD6] uppercase leading-none mb-4">Get Involved</h1>
                    <p className="font-manrope text-[#F5EDD6]/70 text-xl max-w-2xl">
                        Whether you ride, donate, build, or cheer from the sidelines — there's a place for you in the SLSA family.
                    </p>
                </div>
            </section>

            {/* Ways to Help */}
            <section className="py-20 px-4 bg-[#F5EDD6]" data-testid="ways-to-help-section">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="font-marker text-[#C4522A] text-2xl -rotate-1 inline-block mb-4">Your Options</span>
                        <h2 className="font-anton text-4xl sm:text-6xl text-[#3D2008] uppercase">Ways to Help</h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {WAYS_TO_HELP.map((item, i) => {
                            const Inner = (
                                <>
                                    <div className="text-[#C4522A] mb-4">{item.icon}</div>
                                    <h3 className="font-anton text-2xl text-[#3D2008] uppercase mb-3">{item.title}</h3>
                                    <p className="font-manrope text-[#5C4A35] leading-relaxed">{item.desc}</p>
                                    <span className="mt-4 inline-block font-manrope text-sm font-semibold text-[#C4522A] underline underline-offset-2">
                                        {item.scrollTo ? "Sign up below →" : "Learn more →"}
                                    </span>
                                </>
                            );
                            const cls = `bg-white border-2 border-[#3D2008] p-8 shadow-[6px_6px_0px_0px_rgba(45,90,39,0.2)] hover:shadow-[8px_8px_0px_0px_rgba(45,90,39,0.4)] hover:-translate-y-1 transition-all duration-200 block ${i % 2 === 1 ? "sm:mt-6" : ""}`;
                            if (item.scrollTo) {
                                return (
                                    <button key={item.title} type="button" data-testid={item.testId}
                                        onClick={() => document.getElementById(item.scrollTo)?.scrollIntoView({ behavior: "smooth" })}
                                        className={cls + " text-left w-full"}>
                                        {Inner}
                                    </button>
                                );
                            }
                            return item.to ? (
                                <Link key={item.title} to={item.to} data-testid={item.testId} className={cls}>{Inner}</Link>
                            ) : (
                                <div key={item.title} data-testid={item.testId} className={cls}>{Inner}</div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Volunteer Form */}
            <section className="py-20 px-4 bg-[#2D5A27]" data-testid="volunteer-section" id="volunteer-form">
                <div className="max-w-3xl mx-auto">
                    <span className="font-marker text-[#C9A84C] text-2xl -rotate-1 inline-block mb-4">Join the Crew</span>
                    <h2 className="font-anton text-4xl sm:text-6xl text-[#F5EDD6] uppercase mb-6">Volunteer Sign-up</h2>
                    <p className="font-manrope text-[#F5EDD6]/70 text-lg mb-10">
                        Fill out this form and we'll reach out when volunteer opportunities come up. All skills welcome — from event help to construction.
                    </p>

                    {volSuccess ? (
                        <div className="bg-[#C9A84C]/20 border-2 border-[#C9A84C] p-8 text-center" data-testid="volunteer-success">
                            <Check size={40} className="text-[#C9A84C] mx-auto mb-4" />
                            <h3 className="font-anton text-3xl text-[#F5EDD6] uppercase mb-2">You're In the Crew!</h3>
                            <p className="font-manrope text-[#F5EDD6]/70 text-lg">
                                Thanks for volunteering! We'll be in touch soon with opportunities to help.
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleVolSubmit} data-testid="volunteer-form" className="space-y-4">
                            <input type="text" name="website" value={volForm.website || ""} onChange={updateVol("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="font-manrope text-sm font-semibold text-[#F5EDD6]/70 uppercase tracking-wide block mb-1">Name *</label>
                                    <input type="text" placeholder="Your name" value={volForm.name} onChange={updateVol("name")} required data-testid="vol-name-input" className={inputCls} />
                                </div>
                                <div>
                                    <label className="font-manrope text-sm font-semibold text-[#F5EDD6]/70 uppercase tracking-wide block mb-1">Email *</label>
                                    <input type="email" placeholder="your@email.com" value={volForm.email} onChange={updateVol("email")} required data-testid="vol-email-input" className={inputCls} />
                                </div>
                            </div>
                            <div>
                                <label className="font-manrope text-sm font-semibold text-[#F5EDD6]/70 uppercase tracking-wide block mb-1">Phone (optional)</label>
                                <input type="tel" placeholder="(406) 555-0000" value={volForm.phone} onChange={updateVol("phone")} data-testid="vol-phone-input" className={inputCls} />
                            </div>
                            <div>
                                <label className="font-manrope text-sm font-semibold text-[#F5EDD6]/70 uppercase tracking-wide block mb-2">How can you help? (select all that apply)</label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2">
                                    {INTERESTS.map(interest => (
                                        <label key={interest} className="flex items-center gap-3 py-1.5 cursor-pointer group">
                                            <input
                                                type="checkbox"
                                                value={interest}
                                                onChange={(e) => {
                                                    const current = volForm.interests ? volForm.interests.split(", ") : [];
                                                    const updated = e.target.checked
                                                        ? [...current, interest]
                                                        : current.filter(x => x !== interest);
                                                    setVolForm(prev => ({ ...prev, interests: updated.join(", ") }));
                                                }}
                                                className="w-5 h-5 flex-shrink-0 border-2 border-[#C9A84C] rounded-none accent-[#C4522A]"
                                            />
                                            <span className="font-manrope text-sm text-[#F5EDD6]/80 group-hover:text-[#F5EDD6] transition-colors">{interest}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="font-manrope text-sm font-semibold text-[#F5EDD6]/70 uppercase tracking-wide block mb-1">Availability / Notes</label>
                                <textarea
                                    placeholder="When are you available? Any other info you'd like to share?"
                                    value={volForm.availability}
                                    onChange={updateVol("availability")}
                                    rows={3}
                                    data-testid="vol-availability-input"
                                    className="w-full px-4 py-3 bg-white border-2 border-[#3D2008] font-manrope text-[#3D2008] placeholder-[#5C4A35]/50 focus:outline-none focus:border-[#C4522A] focus:shadow-[4px_4px_0px_0px_rgba(196,82,42,0.2)] transition-shadow resize-none"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={volLoading}
                                data-testid="volunteer-submit-btn"
                                className="w-full py-4 bg-[#C4522A] text-white font-anton uppercase tracking-wider text-lg border-2 border-[#C9A84C] shadow-[5px_5px_0px_0px_rgba(201,168,76,0.5)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(201,168,76,0.5)] transition-transform duration-150 disabled:opacity-60 flex items-center justify-center gap-2"
                            >
                                {volLoading && <Loader2 size={18} className="animate-spin" />}
                                {volLoading ? "Signing up..." : "Sign Me Up to Volunteer"}
                            </button>
                        </form>
                    )}
                </div>
            </section>

            {/* Email signup */}
            <section className="py-16 px-4 bg-[#C4522A]" data-testid="subscribe-section">
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="font-anton text-4xl text-white uppercase mb-4">Stay in the Loop</h2>
                    <p className="font-manrope text-white/80 text-lg mb-8">
                        Join our mailing list for updates, events, and park news. No spam — just skatepark progress.
                    </p>
                    <form onSubmit={handleSubscribe} data-testid="subscribe-form" className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                        <input
                            type="text"
                            placeholder="Your name (optional)"
                            value={subName}
                            onChange={e => setSubName(e.target.value)}
                            data-testid="subscribe-name-input"
                            className="flex-1 h-12 px-4 bg-white border-2 border-[#3D2008] font-manrope text-[#3D2008] placeholder-[#5C4A35]/50 focus:outline-none focus:border-[#3D2008]"
                        />
                        <input
                            type="email"
                            placeholder="Email address *"
                            value={subEmail}
                            onChange={e => setSubEmail(e.target.value)}
                            required
                            data-testid="subscribe-email-input"
                            className="flex-1 h-12 px-4 bg-white border-2 border-[#3D2008] font-manrope text-[#3D2008] placeholder-[#5C4A35]/50 focus:outline-none focus:border-[#3D2008]"
                        />
                        <button
                            type="submit"
                            disabled={subLoading}
                            data-testid="subscribe-submit-btn"
                            className="px-6 py-3 bg-[#3D2008] text-[#F5EDD6] font-anton uppercase tracking-wider border-2 border-[#C9A84C] shadow-[4px_4px_0px_0px_rgba(201,168,76,0.4)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform duration-150 disabled:opacity-60"
                        >
                            {subLoading ? "..." : "Join"}
                        </button>
                    </form>
                </div>
            </section>
        </div>
    );
};

export default GetInvolved;
